import * as d3 from 'd3';

/**
 * @brief This is the lean variant of the ZMC-Spider. It will draw a non-interative ZMC Assessment Spider (as in the PPTX) based on the "inputData" supplied.
 * The function will first delete all the content inside the div with the unique ID, then it will draw a non-interactive Version of the ZMC Spider inside this div. The width of this drawing is 100% of the width of the div but max 600px. The "inputData" has to be a "survey structure" type.
 * @param {String} element unique css id selector
 * @param {Object} data survey structure object {categories: [...]}
 * @param {Int} active (optional) indicates which dimension should be displayed as active based on the dimension index
 */
class SpiderChart {
    constructor(options) {
        // load data
        this.data = options.data;
        this.element = d3.select('#' + options.element);
        this.chartID = 'C' + String(Math.floor(Math.random() * 10000 + 1));
        this.language = options.language ?? 'de';

        // check if options.active is defined, if yes use information
        try {
            if (isNaN(options.active)) {
                throw 'Error: options.active is NaN.';
            }
            this.activeElement = parseInt(options.active, 10);
        } catch (e) {
            this.activeElement = -1;
        }

        // define SpiderChart color palette
        this.colorpalette = {
            white: '#FFFFFF',
            lightGrey: 'rgb(241, 242, 246)',
            greenTransparent: 'rgba(12,140,30,0.2)',
            green: 'rgb(12,140,30)',
            orangeTransparent: 'rgba(255, 161, 0, 0.2)',
            orange: 'rgb(255, 161, 0)',
            redTransparent: 'rgba(209, 0, 25, 0.2)',
            red: 'rgb(209, 0, 25)',
            darkGrey: 'rgb(135, 141, 151)',
            lightBlue: 'rgb(215 219 228)',
            darkBlue: 'rgb(56, 83, 112)',
        };

        // rearange json data into two tables
        this._rearange_json_data(options.data);

        // draw chart
        this.draw();
    }

    _rearange_json_data(inputData) {
        // transform data into a useful state for plotting
        this.catData = [];
        this.subcatData = [];

        // maps the axis (index of array) to a segment (value of array)
        this.axisMapping = [];

        var startCounter = 0;
        for (const [i, category] of inputData.categories.entries()) {
            this.catData.push({
                label: this._localize_text(this.language, category.catName),
                start: startCounter,
                range: category.subcatArray.length,
            });
            startCounter += category.subcatArray.length;
            for (let subcategory of category.subcatArray) {
                this.subcatData.push({
                    axis: subcategory.subcatName,
                    userScore: subcategory.subcatUserScore,
                    avgScore: subcategory.subcatAvgScore,
                    bestScore: subcategory.subcatBestScore,
                });
                this.axisMapping.push(i);
            }
        }
    }

    _calculate_svg_size() {
        // get element width
        let elementWidth = this.element.node().getBoundingClientRect().width;

        // calculate all the dimensions based on a predefined drawingarea
        var drawing_area = Math.min(elementWidth, 1000000); //maxwith = 1000 px
        this.width = drawing_area / (1 + (3 / 2) * 0.1);
        this.height = drawing_area / (1 + (3 / 2) * 0.1);
        this.radius = Math.min(this.width, this.height) / 2;
        this.strokeWidth = this.radius / 200;
        this.widthOuterRing = this.radius * 0.1;

        // set the number of levels
        this.levels = 4;

        // allAxis ist an array with the names of all axis (implicitly defines the number of axis)
        this.allAxis = this.subcatData.map(function (i, j) {
            return i.axis;
        });
        this.angleSlice = (Math.PI * 2) / this.allAxis.length; //The width in radians of each "slice"

        // Scale for the radius
        this.rScale = d3.scaleLinear().range([0, this.radius]).domain([0, 4]);
    }

    // This function draws the spider.
    draw() {
        // clean content of element first
        this.element.html('');

        // calculates svg sizes based on the current size of the parent element
        this._calculate_svg_size();

        // create svg
        const svg = this.element.append('svg');
        svg.attr('width', this.width * (1 + (3 / 2) * 0.1));
        svg.attr('height', this.height * (1 + (3 / 2) * 0.1));

        // create plot object
        this.plot = svg
            .append('g')
            .attr(
                'transform',
                'translate(' +
                    (this.width * (1 + (3 / 2) * 0.1)) / 2 +
                    ',' +
                    (this.height * (1 + (3 / 2) * 0.1)) / 2 +
                    ')'
            );

        // draw spider
        this._draw_background_circle();
        this._draw_first_level_segments();
        this._draw_circular_grid();
        this._draw_bestscore_area();
        this._draw_avgscore_area();
        this._draw_userscore_area();
        this._draw_avgscore_line();
        this._draw_bestscore_line();
        this._draw_first_level_arcs();
        this._draw_axes();

        // changes display depending on active element
        if (this.activeElement >= 0 && this.activeElement < this.catData.length) {
            // marks active element
            this._display_as_active(this.activeElement);
        } else if (this.activeElement == -2) {
            // if parameter is set to -2 then make spider hoverable by drawing the invisible segments
            this._draw_invisible_segments();
        } else {
            for (var i = 0; i < this.catData.length; i++) {
                // makes all outer arcs dark blue
                d3.select('#' + this.chartID + '_outerArcID_' + i).style('fill', this.colorpalette.darkBlue);
            }
        }
    }

    _draw_background_circle() {
        var background = this.plot.append('g').attr('class', 'backgroundwrapper');
        //Draw the background circles
        background
            .append('circle')
            .attr('class', 'gridCircle')
            .attr('r', this.radius)
            .style('fill', this.colorpalette.lightGrey);
    }

    _draw_first_level_segments() {
        var drawSegment = d3
            .arc()
            .innerRadius(0)
            .outerRadius(this.radius)
            .startAngle((d) => (d.start - 0.5) * this.angleSlice)
            .endAngle((d) => (d.start + d.range - 0.5) * this.angleSlice);

        var circleSegment = this.plot
            .selectAll('.radarSegment')
            .data(this.catData)
            .enter()
            .append('g')
            .attr('class', 'radarSegment');

        circleSegment
            .append('path')
            .attr('class', 'segmentArea')
            .attr('d', (d) => drawSegment(d))
            .style('fill', this.colorpalette.lightGrey)
            .style('stroke', 'none')
            .attr('id', (d, i) => this.chartID + '_segID_' + i);
    }

    _draw_circular_grid() {
        var circularGrid = this.plot.append('g').attr('class', 'circularGrid');
        //Draw the background circles
        circularGrid
            .selectAll('.levels')
            .data(d3.range(1, this.levels + 1).reverse())
            .enter()
            .append('circle')
            .attr('class', 'gridCircle')
            .attr('r', (d) => (this.radius / this.levels) * d)
            .style('fill', 'none')
            .style('stroke', this.colorpalette.white)
            .style('stroke-width', this.strokeWidth);
    }

    _draw_bestscore_area() {
        // The radial line function
        var radarLineBest = d3
            .radialLine()
            .curve(d3.curveLinearClosed)
            .radius((d) => this.rScale(d.bestScore))
            .angle((d, i) => i * this.angleSlice);

        // Create a wrapper for the blobs
        var blobWrapper = this.plot
            .selectAll('.bestLine')
            .data([this.subcatData])
            .enter()
            .append('g')
            .attr('class', 'bestLine');

        // Append the backgrounds
        blobWrapper
            .append('path')
            .attr('class', 'bestLineArea')
            .attr('d', (d) => radarLineBest(d))
            .style('fill', this.colorpalette.greenTransparent);
    }

    _draw_avgscore_area() {
        // The radial line function
        var radarLineAvg = d3
            .radialLine()
            .curve(d3.curveLinearClosed)
            .radius((d) => this.rScale(d.avgScore))
            .angle((d, i) => i * this.angleSlice);

        // Create a wrapper for the blobs
        var blobWrapper = this.plot
            .selectAll('.avgLine')
            .data([this.subcatData])
            .enter()
            .append('g')
            .attr('class', 'avgLine');

        // Append the backgrounds
        blobWrapper
            .append('path')
            .attr('class', 'avgLineArea')
            .attr('d', (d) => radarLineAvg(d))
            .style('fill', this.colorpalette.orangeTransparent);
    }

    _draw_userscore_area() {
        // The radial line function
        var radarLineUser = d3
            .radialLine()
            .curve(d3.curveLinearClosed)
            .radius((d) => this.rScale(d.userScore))
            .angle((d, i) => i * this.angleSlice);

        // Create a wrapper for the blobs
        var userGraph = this.plot
            .selectAll('.userLine')
            .data([this.subcatData])
            .enter()
            .append('g')
            .attr('class', 'userLine');

        // Append the backgrounds
        userGraph
            .append('path')
            .attr('class', 'radarArea')
            .attr('d', (d) => radarLineUser(d))
            .style('fill', this.colorpalette.red);
    }

    _draw_avgscore_line() {
        // The radial line function
        var radarLineAvg = d3
            .radialLine()
            .curve(d3.curveLinearClosed)
            .radius((d) => this.rScale(d.avgScore))
            .angle((d, i) => i * this.angleSlice);

        // Create a wrapper for the blobs
        var blobWrapper = this.plot
            .selectAll('.avgLineLine')
            .data([this.subcatData])
            .enter()
            .append('g')
            .attr('class', 'avgLineLine');

        // Append the backgrounds
        blobWrapper
            .append('path')
            .attr('class', 'avgLineStroke')
            .attr('d', (d) => radarLineAvg(d))
            .style('fill', 'none')
            .style('stroke', this.colorpalette.orange)
            .style('stroke-width', 2 * this.strokeWidth);
    }

    _draw_bestscore_line() {
        // The radial line function
        var radarLineBest = d3
            .radialLine()
            .curve(d3.curveLinearClosed)
            .radius((d) => this.rScale(d.bestScore))
            .angle((d, i) => i * this.angleSlice);

        // Create a wrapper for the blobs
        var blobWrapper = this.plot
            .selectAll('.bestLineLine')
            .data([this.subcatData])
            .enter()
            .append('g')
            .attr('class', 'bestLineLine');

        // Append the backgrounds
        blobWrapper
            .append('path')
            .attr('class', 'bestLineStroke')
            .attr('d', (d) => radarLineBest(d))
            .style('fill', 'none')
            .style('stroke', this.colorpalette.green)
            .style('stroke-width', 2 * this.strokeWidth);
    }

    _draw_first_level_arcs() {
        var arcLine = d3
            .arc()
            .innerRadius(this.radius + 3)
            .outerRadius(this.radius + 3 + this.widthOuterRing)
            .startAngle((d) => (d.start - 0.5) * this.angleSlice)
            .endAngle((d) => (d.start + d.range - 0.5) * this.angleSlice);

        var arcLayer = this.plot
            .selectAll('.radarArcs')
            .data(this.catData)
            .enter()
            .append('g')
            .attr('class', 'radarArcs');

        var _this = this;

        arcLayer
            .append('path')
            .attr('class', 'arcArea')
            .attr('d', (d) => arcLine(d))
            .style('fill', this.colorpalette.darkGrey)
            .style('stroke', this.colorpalette.white)
            .attr('id', (d, i) => this.chartID + '_outerArcID_' + i)
            .style('stroke-width', 3 * this.strokeWidth)
            .each(function (d, i) {
                //A regular expression that captures all in between the start of a string
                //(denoted by ^) and the first capital letter L
                var firstArcSection = /(^.+?)L/;

                //The [1] gives back the expression between the () (thus not the L as well)
                //which is exactly the arc statement
                var newArc = firstArcSection.exec(d3.select(this).attr('d'))[1];
                //Replace all the comma's so that IE can handle it -_-
                //The g after the / is a modifier that "find all matches rather than
                //stopping after the first match"
                newArc = newArc.replace(/,/g, ' ');

                //Create a new invisible arc that the text can flow along
                var invis = _this.plot.append('g').attr('class', 'textLine');
                invis
                    .append('path')
                    .attr('class', 'hiddenTextArcs')
                    .attr('id', _this.chartID + '_arcID_' + i)
                    .attr('d', newArc)
                    .style('fill', 'none');
            });

        var arcText = this.plot
            .selectAll('.arcText')
            .data(this.catData)
            .enter()
            .append('text')
            .attr('class', 'arcText');

        arcText
            .style('fill', this.colorpalette.white)
            .style('text-shadow', 'none')
            .style('font-size', 0.65 * this.widthOuterRing + 'px')
            .style('font-family', 'sans-serif')
            .attr('dy', 0.72 * this.widthOuterRing)
            .append('textPath')
            .style('text-anchor', 'middle')
            .attr('startOffset', '50%')
            .attr('xlink:href', (d, i) => '#' + this.chartID + '_arcID_' + i)
            .text((d) => d.label);
    }

    _draw_axes() {
        //Create the straight lines radiating outward from the center
        var axis = this.plot.selectAll('.axis').data(this.allAxis).enter().append('g').attr('class', 'axis');
        //Append the lines
        axis.append('line')
            .attr('x1', 0)
            .attr('y1', 0)
            .attr('x2', (d, i) => {
                return this.rScale(4) * Math.cos(this.angleSlice * i - Math.PI / 2);
            })
            .attr('y2', (d, i) => {
                return this.rScale(4) * Math.sin(this.angleSlice * i - Math.PI / 2);
            })
            .attr('class', 'line')
            .style('stroke', this.colorpalette.white)
            .style('stroke-width', this.strokeWidth)
            .attr('id', (d, i) => this.chartID + '_axesID_' + i);
    }

    _draw_invisible_segments() {
        var drawInvisSegment = d3
            .arc()
            .innerRadius(0)
            .outerRadius(this.radius + 3 + this.widthOuterRing)
            .startAngle((d, i) => (i - 0.5) * this.angleSlice)
            .endAngle((d, i) => (i + 0.5) * this.angleSlice);

        var circleInvisSegment = this.plot
            .selectAll('.radarInvisSegment')
            .data(this.subcatData)
            .enter()
            .append('g')
            .attr('class', 'radarInvisSegment');

        circleInvisSegment
            .append('path')
            .attr('class', 'segmentInvisArea')
            .attr('d', (d, i) => drawInvisSegment(d, i))
            .style('fill', 'none')
            .style('stroke', 'none')
            .style('pointer-events', 'all')
            .on('mouseover', (d, i) => {
                d3.select('#' + this.chartID + '_axesID_' + i)
                    .style('stroke', this.colorpalette.darkGrey)
                    .style('stroke-width', 2 * this.strokeWidth);
                d3.select('#' + this.chartID + '_segID_' + this.axisMapping[i]).style(
                    'fill',
                    this.colorpalette.lightBlue
                );
                d3.select('#' + this.chartID + '_outerArcID_' + this.axisMapping[i]).style(
                    'fill',
                    this.colorpalette.darkBlue
                );
            })
            .on('mouseout', (d, i) => {
                d3.select('#' + this.chartID + '_axesID_' + i)
                    .style('stroke', this.colorpalette.white)
                    .style('stroke-width', this.strokeWidth);
                d3.select('#' + this.chartID + '_segID_' + this.axisMapping[i]).style(
                    'fill',
                    this.colorpalette.lightGrey
                );
                d3.select('#' + this.chartID + '_outerArcID_' + this.axisMapping[i]).style(
                    'fill',
                    this.colorpalette.darkGrey
                );
            });
    }

    _display_as_active(i) {
        // change display of active segments
        d3.select('#' + this.chartID + '_segID_' + i).style('fill', this.colorpalette.lightBlue);
        d3.select('#' + this.chartID + '_outerArcID_' + i).style('fill', this.colorpalette.darkBlue);
    }

    /**
     * @brief This function localized the text based on the set language
     * @param {string} language
     * @param {object} languageObject
     * @return {string}
     */
    _localize_text(language, languageObject) {
        switch (language) {
            case 'de':
                return languageObject.de;
            case 'en':
                return languageObject.en;
            default:
                return languageObject.de;
        }
    }
}

export default SpiderChart;
